import { AIBadge, CurrencyCode } from '@buggy/shared'
import { Tooltip } from 'antd'
import { TooltipPropsWithTitle } from 'antd/es/tooltip'
import React, { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import ArrowPaginationButtons, {
  ArrowPaginationButtonsProps,
} from '@/components/common/buttons/arrow-pagination-buttons'
import { Icon } from '@/components/custom-icon'
import { CURRENCY_I18N_MAP } from '@/constants/country-code'
import { useIntl } from '@/i18n/hooks/use-intl'

export interface KolDetailDataBlockHeaderProps {
  anchor?: {
    id: string
    offset?: number
  }
  className?: string
  poweredByAI?: boolean
  currencyCode?: CurrencyCode
  extraContent?: ReactNode
  pagination?: ArrowPaginationButtonsProps
  title: string
  tooltip?: TooltipPropsWithTitle
  hover?: {
    hoverableExtraContent: boolean
    hovered: boolean
  }
}

const KolDetailDataBlockHeader: FunctionComponent<
  KolDetailDataBlockHeaderProps
> = ({
  anchor,
  className,
  title,
  tooltip,
  currencyCode,
  pagination,
  extraContent,
  hover,
  poweredByAI,
}) => {
  const { formatMessage } = useIntl()

  return (
    <>
      {anchor && <Anchor $offset={anchor.offset} id={anchor.id} />}
      <Box className={className}>
        <LeftItems>
          <Title>
            {poweredByAI && <AIBadge />}
            {title}
          </Title>
          {tooltip && (
            <Tooltip {...tooltip}>
              <InfoCircle type='info-circle' />
            </Tooltip>
          )}
        </LeftItems>
        <RightItems>
          {currencyCode && (
            <Currency>
              {formatMessage(
                { id: 'general:displaying_currency' },
                {
                  currencyTitle: formatMessage({
                    id: CURRENCY_I18N_MAP[currencyCode],
                  }),
                  currencyCode,
                },
              )}
            </Currency>
          )}
          {pagination && <ArrowPaginationButtons {...pagination} />}
          {((): ReactNode => {
            if (!hover?.hoverableExtraContent) {
              return extraContent
            }

            if (hover.hoverableExtraContent && hover.hovered) {
              return extraContent
            }

            return null
          })()}
        </RightItems>
      </Box>
    </>
  )
}

const Anchor = styled.div<{ $offset?: number }>`
  pointer-events: none;
  transform: ${({ $offset }): string =>
    $offset ? `translateY(${-$offset}px)` : 'none'};
`

const Box = styled.div`
  display: flex;
  padding: 25px 30px 10px 30px;
  align-items: center;
  justify-content: space-between;

  > :not(:last-child) {
    margin-right: 10px;
  }

  ${ArrowPaginationButtons} {
    gap: 0;
  }
`

const LeftItems = styled.div`
  display: flex;
  align-items: center;
  height: 30px;

  > :not(:last-child) {
    margin-right: 3px;
  }
`

const InfoCircle = styled(Icon)`
  font-size: 18px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%; /* 22.4px */
  color: ${({ theme }): string => theme.colors.gray8};

  // AI Badge
  > div {
    margin-right: 5px;
  }
`

const RightItems = styled.div`
  display: flex;
  align-items: center;
  height: 30px;

  > :not(:last-child) {
    margin-right: 10px;
  }
`

const Currency = styled.div`
  color: ${({ theme }): string => theme.colors.gray7};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  text-wrap: nowrap;
`

export default KolDetailDataBlockHeader
